import { CommonModule } from '@angular/common';
import { Component, TemplateRef, ViewChild, inject } from '@angular/core';
import { MdbModalService } from 'mdb-angular-ui-kit/modal';
import { OfferModalComponent, OfferStory } from '../../../../shared';
import { fadeInUpAnimation } from '../../../../shared/animations';
import { fadeInDownAnimation } from '../../../../shared/animations/fade-in-down.animation';
import { MaintenanceService } from '../../../../core/services/maintenance.service';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

type OfferTier = 'free' | 'silver' | 'gold' | 'platino';

interface TemplateStoryMeta {
  ctaRoute?: string;
  duration?: number;
}

@Component({
  selector: 'app-offer-cards',
  standalone: true,
  imports: [CommonModule],
  providers: [MdbModalService],
  animations: [fadeInUpAnimation, fadeInDownAnimation],
  templateUrl: './offer-cards.component.html',
  styleUrls: ['./offer-cards.component.scss']
})
export class OfferCardsComponent {
  // Template References per le storie
  @ViewChild('freeStory1') freeStory1!: TemplateRef<any>;
  @ViewChild('freeStory2') freeStory2!: TemplateRef<any>;
  @ViewChild('freeStory3') freeStory3!: TemplateRef<any>;
  
  @ViewChild('silverStory1') silverStory1!: TemplateRef<any>;
  @ViewChild('silverStory2') silverStory2!: TemplateRef<any>;
  @ViewChild('silverStory3') silverStory3!: TemplateRef<any>;
  
  @ViewChild('goldStory1') goldStory1!: TemplateRef<any>;
  @ViewChild('goldStory2') goldStory2!: TemplateRef<any>;
  @ViewChild('goldStory3') goldStory3!: TemplateRef<any>;
  
  @ViewChild('platinoStory1') platinoStory1!: TemplateRef<any>;
  @ViewChild('platinoStory2') platinoStory2!: TemplateRef<any>;
  @ViewChild('platinoStory3') platinoStory3!: TemplateRef<any>;

  private modalService = inject(MdbModalService);
  private maintenanceService = inject(MaintenanceService);
  // Imposta su 'template' per utilizzare i template HTML esistenti
  storyDisplayMode: 'template' | 'image' = 'image';
  isMaintenanceMode$: Observable<boolean>;

  private readonly storyDuration: number[] = [15000, 15000, 15000];
  private readonly offerImageStories: Record<OfferTier, OfferStory[]> = {
    free: [
      { type: 'image', imageSrc: 'free-1.png' },
      { type: 'image', imageSrc: 'free-2.png' },
      { type: 'image', imageSrc: 'free-3.png', ctaRoute: '/login' }
    ],
    silver: [
      { type: 'image', imageSrc: 'silver-1.png' },
      { type: 'image', imageSrc: 'silver-2.png' },
      { type: 'image', imageSrc: 'silver-3.png' }
    ],
    gold: [
      { type: 'image', imageSrc: 'gold-1.png' },
      { type: 'image', imageSrc: 'gold-2.png' },
      { type: 'image', imageSrc: 'gold-3.png' }
    ],
    platino: [
      { type: 'image', imageSrc: 'platino-1.png' },
      { type: 'image', imageSrc: 'platino-2.png' },
      { type: 'image', imageSrc: 'platino-3.png' }
    ]
  };
  private readonly offerTemplateStoryMeta: Record<OfferTier, TemplateStoryMeta[]> = {
    free: [{}, {}, {}],
    silver: [{}, {}, {}],
    gold: [{}, {}, {}],
    platino: [{}, {}, {}]
  };

  constructor() {
    this.isMaintenanceMode$ = this.maintenanceService.getMaintenanceStatus$().pipe(
      map(status => status?.isMaintenanceMode ?? false)
    );
  }
  /**
   * Apre la modale per l'offerta FREE
   */
  openFreeOffer(): void {
    this.openOffer('free', [this.freeStory1, this.freeStory2, this.freeStory3]);
  }

  /**
   * Apre la modale per l'offerta SILVER
   */
  openSilverOffer(): void {
    this.openOffer('silver', [this.silverStory1, this.silverStory2, this.silverStory3]);
  }

  /**
   * Apre la modale per l'offerta GOLD
   */
  openGoldOffer(): void {
    this.openOffer('gold', [this.goldStory1, this.goldStory2, this.goldStory3]);
  }

  /**
   * Apre la modale per l'offerta PLATINO
   */
  openPlatinoOffer(): void {
    this.openOffer('platino', [this.platinoStory1, this.platinoStory2, this.platinoStory3]);
  }

  private openOffer(tier: OfferTier, templates: TemplateRef<any>[]): void {
    const duration = [...this.storyDuration];

    if (this.storyDisplayMode === 'image') {
      const stories = this.buildImageStories(tier);
      OfferModalComponent.open(this.modalService, {
        stories,
        fullScreen: false,
        duration,
        modalClass: 'offer-modal-vertical'
      });
      return;
    }

    const stories = this.buildTemplateStories(tier, templates);
    OfferModalComponent.open(this.modalService, {
      stories,
      fullScreen: true,
      duration,
      modalClass: undefined
    });
  }

  private buildImageStories(tier: OfferTier): OfferStory[] {
    return (this.offerImageStories[tier] || []).map(story => ({ ...story }));
  }

  private buildTemplateStories(tier: OfferTier, templates: TemplateRef<any>[]): OfferStory[] {
    const meta = this.offerTemplateStoryMeta[tier] || [];
    return templates.map((template, index) => ({
      type: 'template',
      template,
      ctaRoute: meta[index]?.ctaRoute,
      duration: meta[index]?.duration
    }));
  }
}

